  <template>
    <div class="stats-card" :data-scrolled="isScrolled" :data-hide-total="hideTotal">
      <!-- 上层卡片：渐变背景 - 今日数据 -->
      <div class="stats-card-today">
        <div class="stats-card-item">
          <div class="stats-card-value">{{ formatYesterdayIncome(stats.incomeYesterday).value }}<span class="stats-card-unit">{{ formatYesterdayIncome(stats.incomeYesterday).unit }}</span></div>
          <div class="stats-card-label">昨日收益</div>
        </div>
        <div class="stats-card-item">
          <div class="stats-card-value">{{ formatCapacity(stats.yesterdayCharge).value }}<span class="stats-card-unit">{{ formatCapacity(stats.yesterdayCharge).unit }}</span></div>
          <div class="stats-card-label">昨日充电量</div>
        </div>
        <div class="stats-card-item">
          <div class="stats-card-value">{{ formatCapacity(stats.yesterdayDischarge).value }}<span class="stats-card-unit">{{ formatCapacity(stats.yesterdayDischarge).unit }}</span></div>
          <div class="stats-card-label">昨日放电量</div>
        </div>
      </div>

      <!-- 下层卡片：白色背景 - 累计数据 -->
      <div v-if="!hideTotal" class="stats-card-total">
        <!-- 左侧 -->
        <div class="total-section">
          <div class="stats-card-text">累计收益：{{ formatMoney(stats.totalIncome) }}</div>
          <div class="stats-card-text">实际功率：{{ stats.realPower }}kW</div>
        </div>
        <!-- 分割线 -->
        <div class="stats-card-divider"></div>
        <!-- 右侧 -->
        <div class="total-section">
          <div class="stats-card-text">累计充电：{{ formatterCapacity(stats.totalCharge).split(' ').join('') }}</div>
          <div class="stats-card-text">累计放电：{{ formatterCapacity(stats.totalDischarge).split(' ').join('') }}</div>
        </div>
      </div>
    </div>
  </template>

<script setup>
import { computed } from 'vue'
import { formatterCapacity } from '@/utils/fortmatter'

// 接收滚动状态
const props = defineProps({
  isScrolled: {
    type: Boolean,
    default: false
  },
  realTimeData: {
    type: Object,
    default: () => ({})
  },
  hideTotal: {
    type: Boolean,
    default: false
  }
})

const formatMoney = (val) => {
  if (!val && val !== 0) return '0.00元'
  const value = Number(val)
  if (value >= 10000) {
    return (value / 10000).toFixed(2) + '万元'
  }
  return value.toFixed(2) + '元'
}

// 格式化昨日收益（小于一万元显示元，大于一万元显示万元）
const formatYesterdayIncome = (val) => {
  const value = Number(val || 0)
  if (value >= 10000) {
    return {
      value: (value / 10000).toFixed(2),
      unit: '万元'
    }
  } else {
    return {
      value: value.toFixed(2),
      unit: '元'
    }
  }
}

// 格式化容量（充电量/放电量），拆分值和单位
const formatCapacity = (val) => {
  const formatted = formatterCapacity(val || 0)
  const [value, ...unitParts] = formatted.split(' ')
  return {
    value: value,
    unit: unitParts.join(' ')
  }
}

const stats = computed(() => {
  const rt = props.realTimeData || {}
  return {
    // Top card (Today)
    incomeYesterday: rt.incomeYesterday || 0,
    yesterdayCharge: rt.positivePowerYesterday || 0,
    yesterdayDischarge: rt.reversePowerYesterday || 0,
    // Bottom card (Total)
    totalIncome: rt.incomeSum || 0,
    totalCharge: rt.positivePowerSum || 0,
    realPower: rt.realTimePower || 0,
    totalDischarge: rt.reversePowerSum || 0
  }
})
</script>

<style scoped lang="scss">
.stats-card {
  position: relative;
  width: 500px;
  height: 196px;
  flex-shrink: 0;
  transition: height 0.3s ease;

  // 当隐藏累计数据时，调整高度
  &[data-hide-total="true"] {
    height: 90px;

    .stats-card-today {
      height: 90px;
    }
  }
}

// 上层卡片 - 渐变背景
.stats-card-today {
  position: absolute;
  top: 0;
  left: 0;
  width: 500px;
  height: 196px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 100%);
  border-radius: 30px;
  border: 0.5px solid rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-around;
  /* 均匀分布 */
  align-items: flex-start;
  padding-top: 22px;
  /* 顶部间距 */
  transition: height 0.3s ease;
}

.stats-card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* 居中对齐 */
}


.stats-card-value {
  font-family: 'Gotham', sans-serif;
  font-size: 24px;
  color: #2E2E2E;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.stats-card-unit {
  font-size: 14px;
  font-weight: 325;
}

.stats-card-label {
  margin-top: 8px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.6);
  font-weight: 300;
  line-height: normal;
}

// 下层卡片 - 白色背景
.stats-card-total {
  position: absolute;
  top: 90px;
  left: 0;
  width: 500px;
  height: 106px;
  background: rgba(255, 255, 255, 1);
  border-radius: 30px;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}

.total-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 30px;
  /* 左侧内边距，保持内容对齐 */
}

.stats-card-divider {
  width: 1px;
  height: 45px;
  background-color: #E0E4E7;
  flex-shrink: 0;
}

.stats-card-text {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

// 滚动时隐藏下半部分
.stats-card[data-scrolled="true"] {
  height: 90px;

  .stats-card-today {
    height: 90px;
  }

  .stats-card-total {
    height: 0;
    opacity: 0;
    padding: 0;
  }
}
</style>
